import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { type ReactNode, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import RateLimit from '@/app/sign-in/RateLimitButton';
import SignInPanel from '@/app/sign-in/SignInPanel';
import TermsOfUseLink from '@/app/sign-in/TermsOfUseLink';
import EmailIcon from '@/assets/svg/envelope.svg?react';
import Google from '@/assets/svg/google.svg?react';
import Microsoft from '@/assets/svg/microsoft.svg?react';
import { Button } from '@/components/buttons';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import {
  getPreferredSignInMethod,
  setPendingSignInMethod,
  type SignInMethod,
} from '@/helper/user-preferences';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import { getDiscoveryRedirectUrl } from '../sign-in/discoveryRedirect';
import SignUpTestimonial from './SignUpTestimonial';

type SignUpMethodConfig = {
  id: Exclude<SignInMethod, 'sso'>;
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  rateLimit?: boolean;
};

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stytch = useStytchB2BClient();
  const { pushNotification } = useNotification();

  const redirect = searchParams.get('redirect');
  const discoveryRedirectUrl = getDiscoveryRedirectUrl(true, redirect);
  const preferredMethod = useMemo(() => getPreferredSignInMethod(), []);

  const handleOAuthProviderClick = async (provider: 'google' | 'microsoft') => {
    setPendingSignInMethod(provider);

    try {
      await stytch.oauth[provider].discovery.start({
        discovery_redirect_url: discoveryRedirectUrl,
      });
    } catch (e) {
      pushNotification({
        type: 'error',
        message: getStytchAPIErrorMessage(e, ROUTES.signUp.path),
      });
    }
  };

  const signUpEmailPath = `${ROUTES.signUpEmail.path}${
    searchParams.size > 0 ? `?${searchParams.toString()}` : ''
  }`;

  const handleEmailClick = () => {
    navigate(signUpEmailPath);
  };

  const signUpMethods: SignUpMethodConfig[] = [
    {
      id: 'google',
      label: t`Continue with Google`,
      icon: <Google />,
      onClick: () => handleOAuthProviderClick('google'),
      rateLimit: true,
    },
    {
      id: 'microsoft',
      label: t`Continue with Microsoft`,
      icon: <Microsoft />,
      onClick: () => handleOAuthProviderClick('microsoft'),
      rateLimit: true,
    },
    {
      id: 'email',
      label: t`Continue with email`,
      onClick: handleEmailClick,
      icon: <EmailIcon />,
    },
  ];

  const primaryMethodId = preferredMethod && preferredMethod !== 'sso' ? preferredMethod : 'google';
  const preferredConfig = signUpMethods.find((method) => method.id === primaryMethodId);
  const orderedMethods = preferredConfig
    ? [preferredConfig, ...signUpMethods.filter((method) => method.id !== primaryMethodId)]
    : signUpMethods;

  return (
    <FullPage title={t`Sign Up`}>
      <div className="flex flex-col items-start gap-6 lg:flex-row">
        <SignUpTestimonial />
        <SignInPanel
          title={t`Create a free account with Channel99`}
          footer={
            <>
              <Text variant="caption1" color="grey">
                <Trans>
                  Already have an account?{' '}
                  <Link variant="caption1" color="aqua" to={ROUTES.signIn.path}>
                    Sign in here
                  </Link>
                </Trans>
              </Text>
              <Text variant="caption1" color="grey">
                <Trans>
                  By continuing to sign up I agree to the <TermsOfUseLink />
                </Trans>
              </Text>
            </>
          }
          showGap
        >
          <div className="mx-auto flex w-full max-w-md flex-col gap-4">
            {orderedMethods.map((method) => {
              const isPreferred = primaryMethodId === method.id;

              return (
                <div key={method.id} className="flex w-full flex-col items-center gap-2">
                  {method.rateLimit ? (
                    <RateLimit>
                      <Button
                        variant={isPreferred ? 'primary' : 'secondary'}
                        size="large"
                        block
                        onClick={method.onClick}
                      >
                        {method.icon}
                        {method.label}
                      </Button>
                    </RateLimit>
                  ) : (
                    <Button
                      variant={isPreferred ? 'primary' : 'secondary'}
                      size="large"
                      block
                      onClick={method.onClick}
                    >
                      {method.icon}
                      {method.label}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </SignInPanel>
      </div>
    </FullPage>
  );
};

export default SignUp;
